import { useUser } from '@clerk/react';
import { CalendarBlank, Tray } from '@phosphor-icons/react';
import { useReviewSummary } from '@reelplicate/api-client/approvals';
import { useListCompetitorPosts } from '@reelplicate/api-client/competitors';
import type { QuickstartDto } from '@reelplicate/api-client/model';
import {
  getGetQuickstartQueryKey,
  useDismissQuickstart,
  useGetQuickstart,
} from '@reelplicate/api-client/quickstart';
import { useListUpcomingScheduledPosts } from '@reelplicate/api-client/scheduled-posts';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import { Badge, Button, EmptyState, Skeleton } from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router';

import { CompetitorsEmpty } from '@/features/competitors/competitors-page';
import { usePostActions } from '@/features/competitors/use-post-actions';

import { CompetitorHookRow } from './competitor-hook';
import { DraftCard } from './draft-card';
import { OnboardingChecklist } from './onboarding-checklist';
import { Panel } from './panel';
import { ScheduleRow } from './schedule-row';
import { ThisWeek } from './this-week';

const dateFormat = new Intl.DateTimeFormat(undefined, {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

function greeting(now: Date) {
  const hour = now.getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/**
 * Figma "Screen — Home · Exploration: setup in progress" (140:531). The
 * user's name, today's date, the This week numbers, the drafts needing
 * review, what's coming up, the saved competitor reels and the setup
 * checklist are real. The checklist goes once it's all done or skipped.
 */
export function HomePage() {
  const { user } = useUser();
  const now = new Date();
  const firstName = user?.firstName;
  const { data: workspaces } = useListWorkspaces();
  const workspaceId = workspaces?.[0]?.id ?? '';
  const { data: review } = useReviewSummary(
    workspaceId,
    { limit: 2 },
    { query: { enabled: Boolean(workspaceId) } },
  );
  const waiting = review?.waiting ?? 0;
  const { data: upcoming } = useListUpcomingScheduledPosts(
    workspaceId,
    { limit: 5 },
    { query: { enabled: Boolean(workspaceId) } },
  );
  const { data: winners } = useListCompetitorPosts(
    workspaceId,
    { days: 7, limit: 3 },
    { query: { enabled: Boolean(workspaceId) } },
  );
  const postActions = usePostActions(workspaceId);
  const queryClient = useQueryClient();
  const { data: quickstart } = useGetQuickstart(workspaceId, {
    query: { enabled: Boolean(workspaceId) },
  });
  const dismissQuickstart = useDismissQuickstart();
  const showChecklist =
    quickstart && !quickstart.dismissed && quickstart.completed < quickstart.total;

  function skipSetup() {
    const key = getGetQuickstartQueryKey(workspaceId);
    // Hide it straight away; the refetch brings it back if the save fails.
    queryClient.setQueryData<QuickstartDto>(
      key,
      (old) => old && { ...old, dismissed: true },
    );
    dismissQuickstart.mutate(
      { id: workspaceId },
      { onSettled: () => void queryClient.invalidateQueries({ queryKey: key }) },
    );
  }

  return (
    <div className="flex flex-col gap-8 p-8">
      <header className="flex flex-wrap items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h1 className="text-heading-h1 text-text-primary">
            {greeting(now)}
            {firstName && `, ${firstName}`}
          </h1>
          <p className="text-body-md text-text-secondary">
            {dateFormat.format(now)}
            {waiting > 0 &&
              ` · ${waiting} ${waiting === 1 ? 'draft is' : 'drafts are'} waiting for your review`}
          </p>
        </div>
      </header>

      <div className="flex flex-col gap-6 xl:flex-row xl:items-start">
        <div className="flex min-w-0 flex-1 flex-col gap-6">
          <ThisWeek />

          <Panel
            title="Needs your review"
            badge={
              waiting > 0 && (
                <Badge tone="warning" aria-label={`${waiting} drafts`}>
                  {waiting}
                </Badge>
              )
            }
            subtitle="Generated from your brand profile · checked against your claims"
            link={{ label: 'Open review queue', to: '/flows/drafts' }}
            className="gap-4"
          >
            {!review ? (
              <Skeleton className="h-[196px]" shape="rectangle" />
            ) : review.drafts.length === 0 ? (
              <EmptyState
                icon={Tray}
                title="Nothing to review"
                description="When a flow with Human approval runs, its drafts wait here for you."
                actions={
                  <Button asChild variant="outline">
                    <RouterLink to="/flows">Go to flows</RouterLink>
                  </Button>
                }
              />
            ) : (
              <div className="flex flex-col gap-4 lg:flex-row">
                {review.drafts.map((draft) => (
                  <DraftCard
                    key={draft.item.id}
                    workspaceId={workspaceId}
                    draft={draft}
                  />
                ))}
              </div>
            )}
          </Panel>

          <Panel
            title="What’s working for competitors"
            subtitle="Their posts beating their usual views this week"
            link={{ label: 'See all', to: '/competitors' }}
            className="gap-1"
          >
            {!winners ? (
              <Skeleton className="h-40" shape="rectangle" />
            ) : winners.items.length === 0 ? (
              <CompetitorsEmpty tracking={winners.tracking} />
            ) : (
              <ul>
                {winners.items.map((post) => (
                  <CompetitorHookRow
                    key={post.id}
                    post={post}
                    making={postActions.busyOn(post.id) !== null}
                    onMakeMyVersion={() => postActions.remake(post)}
                    now={now.getTime()}
                  />
                ))}
              </ul>
            )}
          </Panel>
        </div>

        <div className="flex flex-col gap-6 xl:w-100 xl:shrink-0">
          {showChecklist && (
            <OnboardingChecklist tasks={quickstart.items} onDismiss={skipSetup} />
          )}
          <Panel
            title="Coming up"
            subtitle="Your next scheduled posts"
            link={{ label: 'Calendar', to: '/calendar' }}
            className="p-5"
          >
            {!upcoming ? (
              <Skeleton className="h-40" shape="rectangle" />
            ) : upcoming.length === 0 ? (
              <EmptyState
                icon={CalendarBlank}
                title="Nothing scheduled"
                description="Posts you schedule show up here."
                actions={
                  <Button asChild variant="outline">
                    <RouterLink to="/calendar">Go to calendar</RouterLink>
                  </Button>
                }
              />
            ) : (
              <ul>
                {upcoming.map((post) => (
                  <ScheduleRow key={post.id} post={post} />
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}
